/**
 * 슬롯머신 (UNI-Q184 · DESIGN 14.265) — 네 마을의 슬롯상인. 릴은 서버가 정하고, 화면은 돌리다가 한 칸씩 멈춰 보여 준다.
 *
 * 배당은 **판돈의 몇 배**다 — 아이템 개수가 아니다(사용자 「검 90개 날개 300개는 무슨 뜻이야?」). 표는 `world/slot.js`
 * 미러에서 읽는다(숫자를 문장에 손으로 적으면 서버가 바뀐 날 화면만 옛 값을 말한다). 판돈에는 왕도 세금이 더해진다(UNI-Q190).
 */
import { S, esc } from '../../core/state.js';
import {armPendingWrite,checkPendingWrite,clearPendingWrite,pendingWrite,resolvePendingWrite,savePendingWrite} from '../../core/pending-write.js';
import { betQuick, buyBtn, chanceNotice, fold, maxBeforeTax, muted, npcHead, num, taxOf, taxPct, withTax } from './common.js';
import { SLOT_DEFAULTS, SLOT_REEL_WEIGHTS, SLOT_SYMBOL_KO, SLOT_THREE_MULT, SLOT_TWO_SLIME_MULT, slotMaxUnits } from '../../world/slot.js';

/** 릴 그림 — 이모지(모든 기기에서 그려지는 것으로 골랐다). 이름은 SLOT_SYMBOL_KO */
export const SLOT_FACE = { slime: '🟢', potion: '🧪', stone: '💎', sword: '🗡️', wing: '🪶', crown: '👑' };
const ROLL = ['slime', 'potion', 'stone', 'sword', 'wing', 'crown'];

const slotCfg = () => ({ ...SLOT_DEFAULTS, ...(S.slot?.cfg || {}) });
const slotUnit = () => Number(slotCfg().stakeUnit) || SLOT_DEFAULTS.stakeUnit;
/** 판돈 배수 — 1 이상. 한 판 상한은 없다(2026-10-20) — 산술 안전선(slotMaxUnits)만 넘지 않게 한다. 서버가 다시 본다 */
const clampUnits = (n) => {
  const v = Math.floor(Number(n) || 0);
  return Number.isSafeInteger(v) ? Math.min(slotMaxUnits(slotCfg()), Math.max(1, v)) : 1;
};
/** 은화 금액 → 판돈 배수(판돈 단위로 내림, 최소 1배). 칸과 버튼은 은화로 적고 서버에는 배수를 보낸다 */
const unitsOf = (silver) => clampUnits(Math.floor((Number(silver) || 0) / slotUnit()));
/** 잭팟 몫 안내 — 판돈이 기준 이상이면 전부, 아래면 판돈 비율(%) */
function shareText(stake, full) {
  // 태그 없이 — 기금 칸(.racepot)의 굵은 글씨 규칙이 안쪽 <b> 를 큰 줄로 바꿔 문장이 끊겼다
  if (stake >= full) return '공동 기금 전부';
  const pct = (stake / full) * 100;
  return `공동 기금의 ${pct >= 10 ? Math.floor(pct) : pct.toFixed(1)}%`;
}

/** 입력칸과 버튼 글자만 바꾼다 — 패널 전체를 다시 그리면 타이핑 중 포커스가 끊긴다(블랙잭 syncDraft 와 같은 이유) */
function syncDraft(typing = false) {
  const units = clampUnits(S.slotUnits);
  const unit = slotUnit();
  // 타이핑 중에는 칸을 고쳐 쓰지 않는다(「15」를 치는 중에 100,000 으로 바뀌면 못 친다) — 실제 판돈은 아래 줄이 말한다
  const input = document.querySelector('#slotstake');
  if (input && !typing && Number(input.value) !== units * unit) input.value = String(units * unit);
  const pay = document.querySelector('#pbody [data-slot-pay]');
  if (pay) pay.textContent = slotPayText(units * unit);
  document.querySelectorAll('#pbody [data-slot-share]').forEach((el) => {
    el.textContent = shareText(units * unit, slotCfg().fullStake);
  });
  document.querySelectorAll('#pbody [data-slot-fund]').forEach((el) => {
    el.textContent = `${num(S.slot?.fund || 0)} 은화`;
  });
  const btn = document.querySelector('#pbody .slotgo button');
  if (btn && !S.slotBusy) btn.textContent = spinLabel(units * unit);
}
/** 버튼·안내는 실제로 나가는 은화(판돈 + 왕도 세금)를 적는다 */
const spinLabel = (stake, y) => `돌린다 · ${num(withTax(stake, y))} 은화`;
const slotPayText = (stake, y) => `판돈 ${num(stake)} + 왕도 세금 ${num(taxOf(stake, y))} (${taxPct(y)}%) = ${num(withTax(stake, y))} 은화`;
/** 칸에 친 은화 — 판돈 단위로 내린 배수를 쥔다. `typing` 이면 칸 글자는 그대로 둔다(칸을 떠날 때 단위로 맞춘다) */
export function setStake(silver, typing = false) { S.slotUnits = unitsOf(silver); syncDraft(typing); }
/** 간편 버튼 — 은화 금액을 더한다(+10만 · +100만 · +1000만 · +1억 · +10억) */
//   아직 판돈을 안 고른 칸(S.slotUnits 없음)은 0 에서 더한다 — 처음 누른 「+100만」이 110만이 되지 않게
export function addStake(silver) {
  const base = S.slotUnits ? clampUnits(S.slotUnits) : 0;
  S.slotUnits = clampUnits(base + Math.floor((Number(silver) || 0) / slotUnit())); syncDraft();
}

/**
 * 한 판 — 릴을 돌리기 시작하고 서버에 판돈을 보낸다. 응답이 늦으면 원래 판 영수증을 확인한다.
 * 영수증이 없으면 같은 requestId와 판돈으로만 다시 보낸다.
 */
let guard = 0;
export function startSpin(units, send, rerender) {
  if (S.slotBusy) return;
  if (pendingWrite('slot')) { S.slotBusy=true;checkPendingWrite('slot',send,true);rerender();return; }
  const worldN=S.world?.n;
  if(!Number.isSafeInteger(worldN)||worldN<1){S.slotNotice='세계 정보를 확인한 뒤 다시 시도해 달라.';rerender();return;}
  const n = clampUnits(units);
  const message={t:'slotSpin',units:n,requestId:crypto.randomUUID(),worldN};
  if(!savePendingWrite('slot',message)){
    S.slotNotice='판돈 요청을 보관하지 못했다. 다시 접속한 뒤 시도해 달라.';rerender();return;
  }
  if(send(message)!==true){clearPendingWrite('slot',message.requestId);S.slotNotice='판돈 요청을 보내지 못했다. 연결을 확인한 뒤 다시 시도해 달라.';rerender();return;}
  S.slotUnits = n; S.slotBusy = true; S.slotStops = 0; S.slotSpinAt = Date.now(); S.slotNotice='';
  if (S.slotResult) S.slotShown = S.slotResult.reels;
  S.slotResult = null;
  armPendingWrite('slot',send);
  clearTimeout(guard);
  guard = setTimeout(() => { if (pendingWrite('slot')?.requestId===message.requestId) {
    S.slotNotice='응답이 늦어지고 있다. 원래 판 결과를 다시 확인한다.';
    checkPendingWrite('slot',send,true);rerender();
  } }, 10_000);
  rerender();
}

export function recoverSlotSpin(send){
  const row=pendingWrite('slot');
  if(!row)return;
  S.slotBusy=true;S.slotUnits=row.message.units;S.slotNotice='원래 판 결과를 확인 중이다.';
  checkPendingWrite('slot',send);
}

/** 서버 결과 — 최소 0.65초는 돌린 뒤 왼쪽부터 한 칸씩 멈춘다. 이겼으면 소리 */
export function onSlotAck(m, rerender, sound, send) {
  const outcome=resolvePendingWrite('slot',m,send);
  if(outcome==='ignore')return;
  if(outcome==='pending'||outcome==='retrying'){
    S.slotNotice='원래 판 결과를 확인 중이다.';rerender();return;
  }
  clearTimeout(guard);
  S.slotNotice=outcome==='expired'||outcome==='conflict'?'원래 판을 확정하지 못했다. 판돈 기록을 확인해 달라.':m.error||'';
  if (S.slot && Number.isFinite(Number(m.fund))) S.slot.fund = Number(m.fund);
  if (outcome!=='confirmed'||!m.ok) { S.slotBusy = false; S.slotStops = 3; rerender(); return; }
  S.slotResult = m;
  const wait = Math.max(0, 650 - (Date.now() - (S.slotSpinAt || 0)));
  const stop = (k) => {
    if(S.slotResult!==m)return;
    S.slotStops = k;
    if (k >= 3) { S.slotBusy = false; if (m.jackpot || m.win > 0) sound(!!m.jackpot || m.mult >= 26); }
    rerender();
  };
  setTimeout(() => stop(1), wait); setTimeout(() => stop(2), wait + 260); setTimeout(() => stop(3), wait + 520);
}

/** 릴 세 칸 — 돌아가는 칸은 그림 띠가 흐르고, 멈춘 칸은 결과 그림 */
function reels() {
  const r = S.slotResult, stops = S.slotBusy ? (S.slotStops || 0) : 3;
  const shown = r?.reels || S.slotShown || ['crown', 'crown', 'crown'];
  return '<div class="slotreels">' + [0, 1, 2].map((i) => i < stops || !S.slotBusy
    ? `<div class="slotreel stop"><i>${SLOT_FACE[shown[i]] || '❔'}</i></div>`
    : `<div class="slotreel roll"><span>${[...ROLL, ...ROLL].map((s) => `<i>${SLOT_FACE[s]}</i>`).join('')}</span></div>`).join('') + '</div>';
}

/**
 * 지난 판 결과 — **그 판의 판돈**을 함께 적는다. 사용자 2026-09-20 「이거 맞나? 이상한데」: 1억 판의 「판돈의 1배 100,000,000 은화!」가
 * 판돈 칸을 1000(10억)으로 바꾼 뒤에도 남아 10억 판이 1억만 받은 것처럼 보였다. 본전은 이긴 것이 아니다(세금만큼 나갔다).
 */
function resultLine(unit) {
  const r = S.slotResult;
  if (!r || S.slotBusy) return '';
  const stake = Number(r.stake) || 0, tax = Number(r.tax) || 0;
  const head = stake && stake !== clampUnits(S.slotUnits) * unit ? `지난 판(판돈 ${num(stake)}) · ` : `최근 결과 · 판돈 ${num(stake)} 은화 · `;
  if (r.jackpot) return `<div class="slotwin jackpot">${head}👑 왕관 셋! 공동 기금 잭팟을 창고로 보낸다.</div>`;
  if (r.mult === SLOT_TWO_SLIME_MULT) return `<div class="slotwin push">${head}${r.line} — 판돈 ${num(stake)} 은화를 그대로 돌려받았다(본전 · 왕도 세금 ${num(tax)} 은화는 나갔다).</div>`;
  if (r.win > 0) return `<div class="slotwin">${head}${r.line} — 판돈 ${num(stake)} 은화의 <b>${r.mult}배</b>, ${num(r.win)} 은화!</div>`;
  return `<div class="slotwin none">${head}꽝 — 판돈 ${num(stake)} 은화는 공동 기금에 쌓였다.</div>`;
}

function payTable(cfg) {
  const rows = Object.entries(SLOT_THREE_MULT).sort((a, b) => b[1] - a[1])
    .map(([s, m]) => `<div class="kv"><span>${SLOT_FACE[s]}${SLOT_FACE[s]}${SLOT_FACE[s]} ${SLOT_SYMBOL_KO[s]} 셋</span><b>판돈의 ${num(m)}배</b></div>`);
  return fold('배당표 · 같은 그림 셋, 슬라임 둘, 왕관 잭팟', `<div class="kv"><span>👑👑👑 왕관 셋</span><b>공동 기금 잭팟</b></div>`
    + rows.join('')
    + `<div class="kv"><span>🟢 슬라임 둘</span><b>판돈 그대로(${SLOT_TWO_SLIME_MULT}배)</b></div>`
    // Q276 — 한 줄 요약 + [자세히]
    + fold('세 칸이 같으면 배당 · 슬라임 둘이면 본전 · 꽝이면 기금에 쌓인다', `세 칸이 모두 같은 그림이면 그 배당, 슬라임이 둘이면 판돈을 그대로 돌려받는다(본전). 배당은 <b>은화</b>로 준다(아이템이 아니다).<br>`
      + `판돈은 경주·복권·블랙잭과 같이 쓰는 <b>공동 기금</b>에 들어가고, 배당·본전은 기금에서 나간다 — <b>꽝이면 판돈이 그대로 기금에 쌓인다</b>. `
      + `왕관 셋이면 판돈 ${num(cfg.fullStake)} 은화 이상은 기금 전부, 그보다 적으면 판돈 비율만큼 받는다. 한 판 판돈 상한은 없다.`));
}

export function render({ y, town, at }) {
  let h = npcHead() + chanceNotice();
  if (!town) return h + muted('슬롯머신은 네 마을(섬마을·본토·비늘바람·유리샘)의 슬롯상인에게 있다.');
  const d = S.slot;
  if (!d) return h + muted('슬롯상인이 릴을 닦는 중…');
  const cfg = slotCfg();
  const unit = Number(cfg.stakeUnit) || SLOT_DEFAULTS.stakeUnit;
  const units = clampUnits(S.slotUnits), maxUnits = slotMaxUnits(cfg);
  const stake = units * unit;
  const have = Math.max(0, Number(y.silver || 0) + Number(S.vault?.silver || 0));
  // 지난 잭팟 표식(inv.slotJp)은 버튼을 잠그지 않는다 — 창고에 이미 들어갔으면 표식을 지우는 것이 바로 이 버튼의 서버 관문이다
  //   (slotSpin 첫 갈래). 잠그면 합친 세계의 몸은 영영 못 푼다(블랙잭 「지난 판 정리 중」과 같은 고착, 2026-10-20)
  const more = [...(S.slotBusy ? ['돌리는 중'] : []), ...(cfg.on ? [] : ['지금은 쉬는 중'])];
  const jpNote = y.inv?.slotJp ? `<p role="status">지난 잭팟을 정리 중이다 — 돌리기를 누르면 창고 입금을 확인하고 표식을 정리한다.</p>` : '';
  const reelWeight = Object.values(SLOT_REEL_WEIGHTS).reduce((sum, weight) => sum + weight, 0);
  const crownChance = (SLOT_REEL_WEIGHTS.crown / reelWeight) ** 3 * 100;

  const fund = `<div class="racepot slotpot"><span>👑 공동 기금 잭팟</span><b data-slot-fund>${num(d.fund)} 은화</b>
    <small>왕관 셋이 나오면 이 판돈으로 받는 몫: <span data-slot-share>${shareText(stake, cfg.fullStake)}</span>.</small></div>`;
  h += fund;
  h += `<div class="sec slotbox"><h3>${S.slotBusy ? '릴이 멈추면 결과를 확인한다' : '세 칸의 그림을 맞춰 보자'}</h3>${reels()}${resultLine(unit)}${jpNote}${S.slotNotice?`<p role="status">${esc(S.slotNotice)}</p>`:''}${S.slotBusy?'<button class="ghost" onclick="act.slotRecheck()">원래 판 결과 재확인</button>':''}
    <div class="ux-facts"><span class="ux-fact">왕관 셋 확률 <b><span class="ux-number">${crownChance.toFixed(5)}</span><small>%</small></b></span><span class="ux-fact">왕관 셋 보상 <b data-slot-share>${shareText(stake, cfg.fullStake)}</b></span><span class="ux-fact">슬라임 둘 <b>판돈 반환 · 세금 소모</b></span></div>
    <h3>이번 판돈</h3><label for="slotstake" class="qt">${num(unit)} 은화 단위 · 상한 없음</label><div class="row">
      <input id="slotstake" type="number" aria-label="슬롯 판돈 은화" inputmode="numeric" min="${unit}" step="${unit}" value="${stake}"
        oninput="act.slotStake(this.value)" onchange="act.slotStakeSet(this.value)" ${S.slotBusy ? 'disabled' : ''} placeholder="걸 은화 (${num(unit)} 단위)"></div>
    ${/* 전액 — 세금까지 내고 걸 수 있는 가장 큰 판돈(단위로 내림, 최소 1단위) */''}
    ${betQuick('act.slotStakeAdd', `act.slotStakeSet(${Math.max(1, Math.min(maxUnits, Math.floor(maxBeforeTax(have, y) / unit))) * unit})`, { disabled: !!S.slotBusy })}
    ${muted(`<b data-slot-pay>${slotPayText(stake, y)}</b><br>가방 <b>${num(y.silver)}</b> + 창고 <b>${num(S.vault?.silver || 0)}</b> = <b>${num(have)}</b> 은화`)}
    <div class="row slotgo">${buyBtn(S.slotBusy ? '돌리는 중…' : spinLabel(stake, y), 'act.slotSpin()',
      { y, at, need: 'slotSpin', price: withTax(stake, y), more })}</div></div>`;
  return h + payTable(cfg);
}
